import { useNavigate } from "react-router-dom";

import { useWaiting } from "./waiting-context";
import styles from "./shell.module.css";

/**
 * *"n waiting on WhatsApp"* — the ambient signal that something needs a human
 * (STORY-057).
 *
 * ── One number over two queues (HC1) ────────────────────────────────────────
 *
 * ADR-0005 split what "waiting" means: an unvalidated **booking** is a real
 * appointment holding a real chair, and a pending **request** is a proposal
 * holding nothing. Somebody glancing at the header is asking *is there
 * anything*, not *how many of which kind* — so the chip is one number, and the
 * queue it opens has the room to say which is which, and does.
 *
 * The breakdown still rides on the `aria-label`, because a screen reader user
 * gets one pass at this and cannot glance.
 *
 * ── Nothing at zero, and nothing on a failed read ───────────────────────────
 *
 * A chip reading "0 waiting on WhatsApp" is a permanent fixture that says
 * nothing, and the ambient signal only works while its presence *is* the
 * signal. A failed read hides it too, rather than showing the last number it
 * knew: silence is honest, and a header confidently reporting a count that has
 * been wrong for an hour is how somebody stops trusting it at all.
 */
export function WaitingChip(): React.JSX.Element | null {
  const { count } = useWaiting();
  const navigate = useNavigate();

  if (count === null || count.total === 0) return null;

  return (
    <button
      type="button"
      className={styles.waiting}
      data-testid="waiting-chip"
      aria-label={describe(count.awaitingValidation, count.requests)}
      // **The queue lives on the calendar, not on a tab of its own** (HC3) —
      // the story's own first sentence rules out a seventh sidebar item. The
      // hash is what tells the panel to scroll itself into view; React Router
      // does not do it, and a queue the chip drops you above is a chip that
      // does not land anywhere.
      onClick={() => navigate("/calendar#waiting")}
    >
      {count.total} waiting on WhatsApp
    </button>
  );
}

/** What the chip would say if it had room. Read aloud, never drawn. */
function describe(bookings: number, requests: number): string {
  const parts = [
    bookings === 0 ? null : `${String(bookings)} booking${bookings === 1 ? "" : "s"} to validate`,
    requests === 0 ? null : `${String(requests)} request${requests === 1 ? "" : "s"} to decide`,
  ].filter((part): part is string => part !== null);

  return `Waiting on WhatsApp: ${parts.join(", ")}. Open the queue.`;
}
